import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/components/email/EmParts";

const download = (rep) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(rep, null, 1)], { type: "application/json" }));
  const a = document.createElement("a");
  a.href = url; a.download = `tedops-production-report-${rep.generated_at?.slice(0, 10)}.json`; a.click(); URL.revokeObjectURL(url);
};

function Summary({ r }) {
  const c = r.combined_unique;
  return (
    <div className="space-y-2 text-xs" data-testid="prod-report-summary">
      <p className="text-sm">Safety: <b>{r.safety.enabled}</b> on · Test {r.safety.mode_test} · Live {r.safety.mode_live} · live authorisation <b>{r.safety.live_authorised ? "YES" : "no"}</b> · nothing written to the queue: <b>{r.queue_unchanged ? "confirmed" : "CHANGED"}</b></p>
      <p className="text-sm">Unique customers passing consent <b>{c.unique_verified_eligible}</b> · would receive a message <b>{c.unique_would_receive}</b> · messages {c.total_messages} · in several journeys {c.customers_in_multiple_journeys}</p>
      <div className="grid md:grid-cols-2 gap-x-6">{Object.entries(r.activity).map(([k, v]) => <p key={k} className={v.status === "ACTIVITY" ? "text-slate-700" : "text-amber-700"}>{k.replace(/_/g, " ")}: {v.last_14_days ?? "—"} in 14 days · {v.status}</p>)}</div>
      {Object.entries(r.dry_run_groups).map(([g, v]) => <p key={g}><b>{g}</b>: {v.automations.map((a) => `${a.name} ${a.matched}/${a.pass_verified_consent}/${a.would_schedule}`).join(" · ")} <span className="text-slate-400">(matched / pass consent / would schedule)</span></p>)}
      <p>Intervals: {r.intervals.history.length} backed by sales history · {r.intervals.unverified.length} UNVERIFIED · ownership {Object.entries(r.ownership).map(([k, v]) => `${k} ${v}`).join(", ")} · equipment records {r.equipment_records}</p>
    </div>
  );
}

export function ProdReport() {
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get("/email-marketing/production-report").then((r) => setD(r.data)).catch(() => setD({ status: "none" })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (d?.status !== "running") { return undefined; }
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [d, load]);
  const start = async () => { try { await api.post("/email-marketing/production-report"); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3 text-sm" data-testid="prod-report">
      <div className="flex flex-wrap items-center gap-2"><h2 className="text-base font-bold text-slate-900 mr-auto">Production readiness report</h2>
        <Button size="sm" variant="outline" disabled={d?.status === "running"} onClick={start} data-testid="prod-report-run">{d?.status === "running" ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Running…</> : "Run report"}</Button>
        {d?.report && <Button size="sm" variant="ghost" onClick={() => download(d.report)} data-testid="prod-report-download">Download full report</Button>}</div>
      <p className="text-slate-500">Read-only: 14-day activity, safety state, dry runs of the 7 groups, unique customers, intervals, compatibility priorities and ownership. Nothing is scheduled or sent; only the report is saved.</p>
      {d?.status === "failed" && <p className="text-rose-700" data-testid="prod-report-error">Report failed: {d.error}</p>}
      {d?.report && <><p className="text-xs text-slate-400">Generated {d.report.generated_at?.slice(0, 16).replace("T", " ")} by {d.started_by}</p><Summary r={d.report} /></>}
    </section>
  );
}
